import { useState } from 'react';
import { Globe } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useApi } from '../lib/useApi';
import { resetTerms } from '../lib/terms';
import type { FamilyLanguage } from '../lib/languages';

/**
 * Which language you are in, on every page.
 *
 * The family's language decides the greeting, the names of the story types, and
 * which of the ninety-four curricula Play & Learn shows. That is far too much to
 * hang on a control six sections down the family home, where it used to live.
 *
 * A parent can switch between the languages the family has already chosen right
 * here; adding a new one is a bigger decision and stays on the family home.
 */
/** Only these roles belong to a family, so only these have a language to show. */
const FAMILY_ROLES = new Set(['parent', 'grandparent', 'teen', 'child']);

export function LanguageSwitcher() {
  const { user } = useAuth();
  // Staff accounts have no family. Asking anyway answered 403 on every single
  // page they opened — a red console error and a forbidden line in the audit
  // trail for doing nothing wrong. terms.ts already skips this; the switcher did not.
  const inFamily = !!user && FAMILY_ROLES.has(user.role);
  const { data, reload } = useApi<{ languages: FamilyLanguage[] }>(inFamily ? '/family/languages' : null);
  const [busy, setBusy] = useState(false);

  const languages = data?.languages ?? [];
  const primary = languages.find((l) => l.is_primary) ?? languages[0];
  if (!primary) return null;

  const canSwitch = user?.role === 'parent' && languages.length > 1;

  if (!canSwitch) {
    return (
      <Link className="lang-chip" to="/languages" title={`Your family's language: ${primary.name}`}>
        <Globe size={15} strokeWidth={1.75} aria-hidden="true" />
        <span className="yo" lang={primary.code}>
          {primary.native_name || primary.name}
        </span>
      </Link>
    );
  }

  return (
    <label className="lang-chip" title="Switch the language Ìran speaks to you in">
      <Globe size={15} strokeWidth={1.75} aria-hidden="true" />
      <span className="sr-only">Family language</span>
      <select
        value={primary.code}
        disabled={busy}
        onChange={async (e) => {
          const next = e.target.value;
          setBusy(true);
          try {
            await api('/family/languages', { body: { codes: languages.map((l) => l.code), primary: next } });
            // The interface words follow the home language, so the cache must go.
            resetTerms();
            reload();
            // Every page reads the language at mount; a reload is the honest way
            // to make the whole app change together rather than half of it.
            window.location.reload();
          } finally {
            setBusy(false);
          }
        }}
      >
        {languages.map((l) => (
          <option key={l.code} value={l.code}>
            {l.native_name || l.name}
          </option>
        ))}
      </select>
    </label>
  );
}
